import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Sparkles, 
  TrendingUp, 
  PiggyBank,
  Activity,
  MessageSquare,
  X,
  Lightbulb,
  AlertCircle,
  Shield,
  CreditCard,
  Globe,
  ChevronRight,
  Users,
  User,
  Building,
  Phone,
  Plus,
  Landmark,
  Wallet,
  Check,
  Clock,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import { TrustBadge } from "../components/EmptyState";
import { v4 as uuidv4 } from "uuid";

const Explore = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeInsight, setActiveInsight] = useState(null);
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [activeTip, setActiveTip] = useState(null);
  const [bubblesAnimated, setBubblesAnimated] = useState(false);
  
  // Top-up states
  const [showTopUp, setShowTopUp] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showVerifyCard, setShowVerifyCard] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [topUpAmount, setTopUpAmount] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [cvvInput, setCvvInput] = useState("");
  const [pendingVerification, setPendingVerification] = useState(null);
  const [verificationAmount, setVerificationAmount] = useState("");
  const [cardForm, setCardForm] = useState({
    card_number: "",
    expiry: "",
    cvv: "",
    cardholder_name: ""
  });

  // Fetch recipients (people/accounts user has sent money to)
  const { data: recipientsData } = useQuery({
    queryKey: ["recipients"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/recipients`);
        return response.data;
      } catch {
        // Return mock data if endpoint doesn't exist
        return { recipients: [] };
      }
    },
    staleTime: 60000,
  });

  const recipients = recipientsData?.recipients || [];

  // Fetch wallets for JMD Wallet display
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  const jmdWallet = wallets?.find(w => w.currency === "JMD");

  const formatCurrency = (amount) => {
    return `J$${parseFloat(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  // Fetch payment methods (verified cards)
  const { data: paymentMethods, refetch: refetchMethods } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: async () => {
      const response = await axios.get(`${API}/topup/payment-methods`);
      return response.data;
    }
  });

  // Fetch pending verification methods
  const { data: pendingMethods, refetch: refetchPending } = useQuery({
    queryKey: ["pendingPaymentMethods"],
    queryFn: async () => {
      const response = await axios.get(`${API}/topup/payment-methods/pending`);
      return response.data;
    }
  });

  // Add new card mutation
  const addMethodMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/topup/payment-method`, cardForm);
      return response.data;
    },
    onSuccess: () => {
      refetchPending();
      setShowAddCard(false);
      setCardForm({ card_number: "", expiry: "", cvv: "", cardholder_name: "" });
      // Show pending message and redirect
      setSuccessMessage("Card added! A small verification charge has been made. Please verify within 24 hours.");
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage("");
      }, 4000);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add card");
    }
  });

  // Verify card mutation
  const verifyMethodMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/topup/payment-method/verify`, {
        method_id: pendingVerification.method_id,
        verification_amount: parseFloat(verificationAmount)
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentMethods"]);
      refetchPending();
      setShowVerifyCard(false);
      setPendingVerification(null);
      setVerificationAmount("");
      toast.success("Card verified successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  });

  // Top-up mutation
  const topUpMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/topup`, {
        amount: parseFloat(topUpAmount),
        currency: "JMD",
        payment_method_id: selectedMethod,
        cvv: cvvInput,
        idempotency_key: uuidv4()
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      setShowTopUp(false);
      setTopUpAmount("");
      setCvvInput("");
      setSelectedMethod(null);
      // Show success and redirect after 4 seconds
      setSuccessMessage(`J$${data.net_amount?.toLocaleString() || topUpAmount} added to your wallet!`);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage("");
      }, 4000);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Top-up failed");
    }
  });

  // Trigger bubble animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setBubblesAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const { data: healthScore } = useQuery({
    queryKey: ["healthScore"],
    queryFn: async () => {
      const response = await axios.get(`${API}/ai/health-score`);
      return response.data;
    }
  });

  const insightMutation = useMutation({
    mutationFn: async (type) => {
      const response = await axios.post(`${API}/ai/insights`, { insight_type: type });
      return response.data;
    },
    onSuccess: (data, type) => {
      // Parse the insights into short summary and bullet points
      const parsed = parseInsight(data.insights, type);
      setActiveInsight({ ...data, ...parsed, type });
      setShowInsightModal(true);
    }
  });

  // Parse AI response into summary + bullets
  const parseInsight = (text, type) => {
    const lines = text.split('\n').filter(l => l.trim());
    const summary = lines[0]?.slice(0, 80) + (lines[0]?.length > 80 ? '...' : '') || "Analysis complete.";
    
    // Extract key points (max 3)
    const bullets = lines.slice(1, 4).map(l => 
      l.replace(/^[-•*]\s*/, '').trim().slice(0, 100)
    ).filter(Boolean);
    
    // Generate actionable tip based on type
    const actionTips = {
      spending: "Review your top 3 spending categories this week.",
      savings: "Set up a J$500 weekly auto-transfer to your Smart Pot."
    };
    
    return {
      summary: summary.replace(/^[-•*]\s*/, ''),
      bullets: bullets.length ? bullets : ["Your finances are on track.", "Keep monitoring your habits."],
      actionTip: actionTips[type] || "Continue building healthy financial habits."
    };
  };

  // Insight tiles - Removed "Portfolio Advice" and "Health Score"
  const insightTypes = [
    { 
      id: "spending", 
      icon: Activity, 
      label: "Spending Analysis", 
      shortLabel: "Track your spending patterns.",
      color: "text-blue-400 bg-blue-400/10" 
    },
    { 
      id: "savings", 
      icon: PiggyBank, 
      label: "Saving Tips", 
      shortLabel: "Smart ways to save more.",
      color: "text-green-400 bg-green-400/10" 
    }
  ];

  // Quick tips with short display text
  const quickTips = [
    { id: 1, short: "Round-ups", full: "Set up automatic round-ups to save without thinking. Small amounts add up over time." },
    { id: 2, short: "Weekly check", full: "Review your spending patterns weekly. This habit can improve your overall financial health." },
    { id: 3, short: "Emergency fund", full: "Keep 3-6 months of expenses in your emergency fund for peace of mind." },
    { id: 4, short: "Bill reminders", full: "Set payment reminders to avoid late fees and maintain a good financial standing." },
    { id: 5, short: "Goal setting", full: "Set specific savings goals. Having a target makes saving feel more rewarding." }
  ];

  // Bubble positions for floating animation - adjusted for avatar on left
  const bubblePositions = [
    { top: "8%", left: "45%", delay: "0s", color: "from-violet-500/30 to-purple-600/20" },
    { top: "20%", left: "70%", delay: "0.2s", color: "from-blue-500/30 to-cyan-600/20" },
    { top: "45%", left: "55%", delay: "0.4s", color: "from-teal-500/30 to-emerald-600/20" },
    { top: "30%", left: "85%", delay: "0.1s", color: "from-pink-500/30 to-rose-600/20" },
    { top: "60%", left: "65%", delay: "0.3s", color: "from-indigo-500/30 to-blue-600/20" }
  ];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="explore-page">
      <header className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Wallet</h1>
      </header>

      <main className="px-6 space-y-6">
        {/* JMD Wallet Section */}
        <div className="glass rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">JMD Wallet</p>
                <p className="text-lg font-mono font-semibold text-primary">
                  {formatCurrency(jmdWallet?.available_balance || 0)}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowTopUp(true)}
              className="btn-primary"
              size="sm"
              data-testid="add-funds-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Funds
            </Button>
          </div>
          
          {/* Pending Verification Cards */}
          {pendingMethods && pendingMethods.length > 0 && (
            <div className="border-t border-white/10 pt-3 mt-3">
              <p className="text-xs uppercase tracking-widest text-yellow-400/70 mb-2">
                Pending Verification
              </p>
              {pendingMethods.map((method) => (
                <div key={method.method_id} className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-mono">{method.brand} •••• {method.last4}</span>
                    </div>
                    <span className="text-xs text-yellow-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {method.hours_remaining}h left
                    </span>
                  </div>
                  <Button
                    size="sm"
                    className="w-full btn-secondary"
                    onClick={() => {
                      setPendingVerification(method);
                      setShowVerifyCard(true);
                    }}
                  >
                    Verify Now
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Insights - Short Tiles */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-medium text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI Insights
            </h3>
          </div>
          <TrustBadge
            icon={Shield}
            text="Insights are generated privately. Your data never leaves Stelx."
            variant="security"
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            {insightTypes.map((type) => {
              const Icon = type.icon;
              const isLoading = insightMutation.isPending && insightMutation.variables === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => insightMutation.mutate(type.id)}
                  disabled={insightMutation.isPending}
                  className={`glass rounded-2xl p-4 text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                    isLoading ? "opacity-50" : ""
                  }`}
                  data-testid={`insight-${type.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${type.color}`}>
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <p className="font-medium text-sm text-foreground">{type.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{type.shortLabel}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ask Your Coach - Navigate to dedicated page */}
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">Ask Your Coach</p>
              <p className="text-xs text-muted-foreground">Get personalized guidance</p>
            </div>
          </div>
          <Button
            className="w-full btn-primary"
            onClick={() => navigate("/coach")}
            data-testid="ask-coach-btn"
          >
            Start Conversation
          </Button>
        </div>

        {/* Virtual Card & Remittance Options */}
        <div className="space-y-3">
          <button
            onClick={() => navigate("/card")}
            className="w-full glass rounded-2xl p-5 flex items-center justify-between transition-all hover:bg-white/10"
            data-testid="virtual-card-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-400/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-violet-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Virtual Card</p>
                <p className="text-xs text-muted-foreground">Shop online securely</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate("/remittance")}
            className="w-full glass rounded-2xl p-5 flex items-center justify-between transition-all hover:bg-white/10"
            data-testid="remittance-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-400/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Remittance</p>
                <p className="text-xs text-muted-foreground">Send money internationally</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Recipients Section */}
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Recipients</h3>
          <div className="space-y-3">
            {/* Stelx Contacts */}
            <button
              onClick={() => navigate("/contacts")}
              className="w-full glass rounded-2xl p-5 flex items-center justify-between transition-all hover:bg-white/10"
              data-testid="stelx-contacts-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">Stelx Contacts</p>
                  <p className="text-xs text-muted-foreground">Send to saved Stelx users</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Bank Accounts */}
            <button
              onClick={() => navigate("/bank-accounts")}
              className="w-full glass rounded-2xl p-5 flex items-center justify-between transition-all hover:bg-white/10"
              data-testid="bank-accounts-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-400/10 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-blue-400" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">Bank Accounts</p>
                  <p className="text-xs text-muted-foreground">Send to external bank accounts</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </main>

      {/* Insight Modal (Bottom Sheet) */}
      {showInsightModal && activeInsight && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center"
          onClick={() => setShowInsightModal(false)}
        >
          <div 
            className="w-full max-w-lg bg-card/95 backdrop-blur-2xl rounded-t-3xl border-t border-white/10 p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-4" />
            
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {activeInsight.type === "spending" && <Activity className="w-5 h-5 text-blue-400" />}
                {activeInsight.type === "savings" && <PiggyBank className="w-5 h-5 text-green-400" />}
                <h3 className="font-heading font-semibold">
                  {activeInsight.type === "spending" ? "Spending Analysis" : "Saving Tips"}
                </h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowInsightModal(false)}
                className="text-muted-foreground"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Bullet Points (max 3) */}
            <ul className="space-y-3 mb-4">
              {activeInsight.bullets.slice(0, 3).map((bullet, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">{i + 1}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{bullet}</p>
                </li>
              ))}
            </ul>

            {/* Actionable Tip */}
            <div className="glass rounded-xl p-4 border border-primary/20">
              <p className="text-xs uppercase tracking-widest text-primary mb-2">Action</p>
              <p className="text-sm font-medium">{activeInsight.actionTip}</p>
            </div>

            <p className="text-[10px] text-muted-foreground/50 mt-4 text-center">
              This insight can improve your overall financial health.
            </p>
          </div>
        </div>
      )}

      {/* Top-up Modal */}
      {showTopUp && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowTopUp(false);
            setTopUpAmount("");
            setCvvInput("");
            setSelectedMethod(null);
          }}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-center mb-2">
              Add Funds
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Top up your JMD wallet using a debit or credit card
            </p>

            {/* Amount Input */}
            <div className="mb-4">
              <Label>Amount (JMD)</Label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
                <Input
                  type="number"
                  placeholder="0.00"
                  className="input-glass pl-10 font-mono text-lg"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  data-testid="topup-amount-input"
                />
              </div>
              {/* Quick amounts */}
              <div className="flex gap-2 mt-2">
                {[1000, 5000, 10000].map((amt) => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    className="flex-1 btn-secondary text-xs"
                    onClick={() => setTopUpAmount(amt.toString())}
                  >
                    J${amt.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>

            {/* Select Payment Method */}
            {paymentMethods && paymentMethods.length > 0 ? (
              <div className="mb-4">
                <Label>Select Card</Label>
                <div className="space-y-2 mt-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.method_id}
                      onClick={() => setSelectedMethod(method.method_id)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        selectedMethod === method.method_id 
                          ? "bg-primary/20 border-primary/50" 
                          : "bg-white/5 border-white/10 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-primary" />
                        <span className="font-mono text-sm">{method.brand} •••• {method.last4}</span>
                      </div>
                      {selectedMethod === method.method_id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </button>
                  ))}
                </div>

                {/* CVV Input for selected card */}
                {selectedMethod && (
                  <div className="mt-4">
                    <Label>CVV / Security Code</Label>
                    <Input
                      type="password"
                      maxLength={4}
                      placeholder="•••"
                      className="input-glass mt-2 text-center font-mono tracking-widest"
                      value={cvvInput}
                      onChange={(e) => setCvvInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      data-testid="cvv-input"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-4 text-center py-4 glass rounded-xl">
                <CreditCard className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No cards on file</p>
                <p className="text-xs text-muted-foreground/70">Add a card to top up your wallet</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 btn-secondary"
                onClick={() => setShowAddCard(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Card
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={() => topUpMutation.mutate()}
                disabled={!topUpAmount || !selectedMethod || !cvvInput || topUpMutation.isPending}
                data-testid="confirm-topup-btn"
              >
                {topUpMutation.isPending ? "Processing..." : "Top Up"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCard && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowAddCard(false);
            setCardForm({ card_number: "", expiry: "", cvv: "", cardholder_name: "" });
          }}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-center mb-2">
              Add Debit/Credit Card
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              We'll make a small refundable charge (J$10-50) for verification
            </p>

            <div className="space-y-4">
              <div>
                <Label>Cardholder Name</Label>
                <Input
                  placeholder="Name on card"
                  className="input-glass mt-2"
                  value={cardForm.cardholder_name}
                  onChange={(e) => setCardForm({ ...cardForm, cardholder_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Card Number</Label>
                <Input
                  placeholder="1234 5678 9012 3456"
                  className="input-glass mt-2 font-mono"
                  value={cardForm.card_number}
                  onChange={(e) => setCardForm({ ...cardForm, card_number: e.target.value.replace(/\D/g, "").slice(0, 16) })}
                  maxLength={16}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Expiry</Label>
                  <Input
                    placeholder="MM/YY"
                    className="input-glass mt-2 font-mono"
                    value={cardForm.expiry}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, "");
                      if (val.length > 2) val = val.slice(0, 2) + "/" + val.slice(2, 4);
                      setCardForm({ ...cardForm, expiry: val });
                    }}
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label>CVV</Label>
                  <Input
                    type="password"
                    placeholder="•••"
                    className="input-glass mt-2 font-mono"
                    value={cardForm.cvv}
                    onChange={(e) => setCardForm({ ...cardForm, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    maxLength={4}
                  />
                </div>
              </div>
            </div>

            <div className="glass rounded-xl p-3 mt-4 border border-yellow-500/20 bg-yellow-500/5">
              <p className="text-xs text-yellow-400">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                You'll have 24 hours to verify the charge amount on your card statement.
              </p>
            </div>

            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                className="flex-1 btn-secondary"
                onClick={() => {
                  setShowAddCard(false);
                  setCardForm({ card_number: "", expiry: "", cvv: "", cardholder_name: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={() => addMethodMutation.mutate()}
                disabled={!cardForm.card_number || !cardForm.expiry || !cardForm.cvv || !cardForm.cardholder_name || addMethodMutation.isPending}
                data-testid="add-card-submit-btn"
              >
                {addMethodMutation.isPending ? "Adding..." : "Add Card"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Verify Card Modal */}
      {showVerifyCard && pendingVerification && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowVerifyCard(false);
            setPendingVerification(null);
            setVerificationAmount("");
          }}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-center mb-2">
              Verify Your Card
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Enter the exact charge amount from your card statement
            </p>

            <div className="glass rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <span className="font-mono">{pendingVerification.brand} •••• {pendingVerification.last4}</span>
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {pendingVerification.hours_remaining ? `${pendingVerification.hours_remaining} hours remaining` : "24 hours to verify"}
              </p>
            </div>

            <div className="mb-4">
              <Label>Verification Amount (JMD)</Label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  className="input-glass pl-10 font-mono"
                  value={verificationAmount}
                  onChange={(e) => setVerificationAmount(e.target.value)}
                  data-testid="verification-amount-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 btn-secondary"
                onClick={() => {
                  setShowVerifyCard(false);
                  setPendingVerification(null);
                  setVerificationAmount("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={() => verifyMethodMutation.mutate()}
                disabled={!verificationAmount || verifyMethodMutation.isPending}
                data-testid="verify-card-btn"
              >
                {verifyMethodMutation.isPending ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 text-center animate-slide-up">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="font-heading font-semibold text-xl mb-2">Success!</h3>
            <p className="text-sm text-muted-foreground">{successMessage}</p>
            <p className="text-xs text-muted-foreground/70 mt-4">Redirecting...</p>
          </div>
        </div>
      )}

      <NavBar active="wallet" />
    </div>
  );
};

export default Explore;
